import { getArenaConfig } from '@shared/utils/getArenaConfig';
import laroute from '@laroute';
import storage from '@shared/js/storage';
import { httpClient } from '@shared/js/api/httpClient';

const LOGIN_NOTIFICATION_COOKIE = 'asport.loginNotification';

const fetchProfile = async() => {
	const params = {};

	if (window.loadBookmarks) {
		params.loadBookmarks = true;
	}

	if (window.loadFollowings) {
		params.loadFollowings = true;
	}

	if (window.arenaParams?.paymentsEnabled) {
		params.loadPayments = true;
	}

	if (window.isEmbed) {
		params.isEmbed = true;
	}

	return httpClient.getUserProfile(params);
};

const hasExternalAccessForEvent = (accessibleResources, event) => {
	return accessibleResources?.eventIds?.external?.includes(parseInt(event.id));
};

const hasSubscriptionForEvent = (accessibleResources, event) => {
	return accessibleResources?.eventIds?.subscription?.includes(parseInt(event.id));
};

const hasPpv = (accessibleResources, event) => {
	return accessibleResources?.eventIds?.bought?.includes(parseInt(event.id));
};

const getInitialState = () => ({
	profile:              null,
	isInitialized:        false,
	subscriptions:        null,
	purchasedEvents:      null,
	hasAccessToEvent:     null,
	fanAccountGeoblocked: false,
	preferences:          {
		// we set a default here to avoid a flash of the noSpoilerMode while the profile is loading
		// also if the user is logged out, we want to show the results
		noSpoilerMode: !getArenaConfig().loadArenaProfile || !getArenaConfig().enableNoSpoilerMode
			? false
			: storage.getLocalStorageItem('noSpoilerMode') === 'true',
	},
});

export const mutations = {
	FLUSH_PROFILE:             'flushProfile',
	SET_PROFILE:               'setProfile',
	SET_NO_SPOILER_MODE:       'setNoSpoilerMode',
	SET_INITIALIZED:           'setInitialized',
	SET_LOGIN_REDIRECT_URL:    'setLoginRedirectUrl',
	SET_FANACCOUNT_GEOBLOCKED: 'setFanaccountGeoblocked',
	ADD_BOOKMARK:              'addBookmark',
	REMOVE_BOOKMARK:           'removeBookmark',
};

export const actions = {
	LOGOUT:                    'logout',
	LOAD_PROFILE:              'loadProfile',
	SET_INITIALIZED:           'setInitialized',
	REMOVE_LOGIN_NOTIFICATION: 'removeLoginNotification',
	UPDATE_PROFILE:            'updateProfile',
	UPDATE_NO_SPOILER_MODE:    'updateNoSpoilerMode',
	SET_LOCALE:                'setLocale',
	CHECK_ARENA_GEOBLOCKING:   'checkArenaGeoblocking',
	BOOKMARK_EVENT:            'bookmarkEvent',
	UNBOOKMARK_EVENT:          'unbookmarkEvent',
};

export default {
	namespaced: true,
	state:      getInitialState,
	mutations:  {
		[mutations.FLUSH_PROFILE](state) {
			state.profile = null;
			sessionStorage.removeItem('viewerId');
		},
		[mutations.SET_PROFILE](state, { profile }) {
			state.profile = profile;

			if (
				profile.preferences &&
				Object.prototype.hasOwnProperty.call(profile.preferences, 'noSpoilerMode') &&
				getArenaConfig().enableNoSpoilerMode // only set the noSpoilerMode if it is enabled in the config
			) {
				storage.setLocalStorageItem('noSpoilerMode', profile.preferences.noSpoilerMode);
				state.preferences.noSpoilerMode = profile.preferences.noSpoilerMode;
			}

			if (profile.viewerId) {
				sessionStorage.setItem('viewerId', profile.viewerId);
			}
		},
		[mutations.SET_NO_SPOILER_MODE](state, value) {
			if (getArenaConfig().enableNoSpoilerMode) {
				state.preferences.noSpoilerMode = value;
				storage.setLocalStorageItem('noSpoilerMode', value ? 'true' : 'false');
			}
		},
		[mutations.SET_INITIALIZED](state) {
			state.isInitialized = true;
		},
		[mutations.SET_LOGIN_REDIRECT_URL](redirectUrl) {
			storage.loginRedirectUrl = redirectUrl;
		},
		[mutations.SET_FANACCOUNT_GEOBLOCKED](state) {
			state.fanAccountGeoblocked = true;
		},
		[mutations.ADD_BOOKMARK](state, { event }) {
			if (!state.profile.bookmarks) {
				state.profile.bookmarks = [];
			}
			// Check if bookmark already exists to avoid duplicates
			const exists = state.profile.bookmarks.some(bookmark => bookmark.id === event.id && bookmark.type === 'event');
			if (exists) {
				return;
			}
			// Create a new array to ensure reactivity (like REMOVE_BOOKMARK does)
			state.profile.bookmarks = [
				...state.profile.bookmarks,
				{
					id:   event.id,
					type: 'event',
				},
			];
		},
		[mutations.REMOVE_BOOKMARK](state, { event }) {
			state.profile.bookmarks = state.profile.bookmarks.filter(bookmark => bookmark.id !== event.id || bookmark.type !== 'event');
		},
	},
	actions: {
		async [actions.LOAD_PROFILE]({ commit, rootGetters }, { forceReload = false } = {}) {
			if (rootGetters['features/isProfileDisabled']) {
				commit(mutations.SET_INITIALIZED);
				return;
			}

			// Both Arena and the Player are requesting the profile. Whichever comes first,
			// actually fetches it, other awaits the same promise
			try {
				if (!window.userProfilePromise || forceReload) {
					window.userProfilePromise = fetchProfile();
				}
				const response = await window.userProfilePromise;
				const profile = { ...response.data };
				const queryParams = new URLSearchParams(window.location.search);
				const embedLoginFailed = queryParams.has('embedLoginFailed');
				const hashContainsLoginRequired = window.location.hash.includes('login_required');
				const queryContainsLoginRequired = queryParams.has('login_required');

				// SSO redirect handling for unauthenticated users
				if (profile.redirectUrl && !hashContainsLoginRequired && !queryContainsLoginRequired && !embedLoginFailed) {
					window.location.href = profile.redirectUrl;
					return;
				}

				if (profile && profile.id) {
					commit(mutations.SET_PROFILE, { profile });
				} else {
					commit(mutations.FLUSH_PROFILE); // Logged out
				}
			} catch (error) {
				window.userProfilePromise = null;
				commit(mutations.FLUSH_PROFILE);
			} finally {
				commit(mutations.SET_INITIALIZED);
			}
		},
		[actions.SET_INITIALIZED]({ commit }) {
			commit(mutations.SET_INITIALIZED);
		},
		[actions.LOGOUT]({ dispatch, state }) {
			dispatch(actions.REMOVE_LOGIN_NOTIFICATION);
			httpClient.logout(state.profile)
				.then((response) => {
					if (response?.data?.redirectUrl) {
						window.location.href = response.data.redirectUrl;
					} else {
						window.location.reload();
					}
				})
				.catch(() => {
					window.toast.error(window.$I18n.trans('errors.500.title'));
				});
		},
		[actions.REMOVE_LOGIN_NOTIFICATION]() {
			window.$cookies.remove(LOGIN_NOTIFICATION_COOKIE, null, window.sessionDomain);
		},
		async [actions.UPDATE_PROFILE]({ commit }, profile) {
			const bodyData = profile;
			if (profile.preferences) {
				bodyData.preferences = JSON.stringify(profile.preferences);
			}

			const response = await httpClient.updateUserProfile(bodyData);

			commit(mutations.SET_PROFILE, { profile: response.data });
		},
		async [actions.UPDATE_NO_SPOILER_MODE]({ commit }, value) {
			// Update the preference on the server
			await httpClient.updateUserProfile({
				preferences: JSON.stringify({ noSpoilerMode: value }),
			});

			// Only update the local state, don't replace the entire profile
			// This preserves accessibleResources, bookmarks, followings, etc.
			commit(mutations.SET_NO_SPOILER_MODE, value);
		},
		async [actions.SET_LOCALE]({ _commit }, locale) {
			await httpClient.setUserLocale(locale);
			const targetUrl = new URL(window.location.href);
			targetUrl.searchParams.set('locale', locale);
			window.location.href = targetUrl.toString();
		},
		async [actions.CHECK_ARENA_GEOBLOCKING]({ commit }) {
			const response = await httpClient.checkArenaGeoblocking();

			if (response.data.fanaccount && response.data.fanaccount === 'blocked') {
				commit(mutations.SET_FANACCOUNT_GEOBLOCKED);
			}
		},
		async [actions.BOOKMARK_EVENT]({ commit }, { event, unsave = false }) {
			try {
				if (unsave) {
					commit(mutations.REMOVE_BOOKMARK, { event });
					await httpClient.removeBookmark('event', event.id);
				} else {
					commit(mutations.ADD_BOOKMARK, { event });
					await httpClient.addBookmark('event', event.id);
				}
				// API call successful, keep the optimistic update
			} catch (error) {
				if (unsave) {
					commit(mutations.ADD_BOOKMARK, { event });
				} else {
					commit(mutations.REMOVE_BOOKMARK, { event });
				}
			}
		},
		async [actions.UNBOOKMARK_EVENT]({ dispatch }, { event }) {
			await dispatch(actions.BOOKMARK_EVENT, { event, unsave: true });
		},
	},
	getters: {
		profile:    (state) => state.profile,
		profileUrl: (state, _getters, _rootState, rootGetters) => {
			if (state.profile?.externalProfileUrl) {
				return state.profile.externalProfileUrl;
			}
			if (state.profile?.canUpdateContext) {
				return laroute.route('manager.user.edit');
			}
			if (state.profile?.isFanAccount) {
				// On mobile we have an navigation site on /acccount
				// on Desktop that site redirects to /account/profile
				if (rootGetters['device/isMobileViewport']) {
					return laroute.route('arena.fan_account');
				}
				return laroute.route('arena.fan_account') + '/profile';
			}
		},
		isExternalProfileUrl: (state) => state.profile?.externalProfileUrl !== null,
		preferences:          (state) => state.preferences,
		userId:               (state) => state.profile?.id,
		context:              (state) => state.profile && state.profile.context ? state.profile.context : null,
		isLoggedIn:           (state) => state.profile !== null,
		isInitialized:        (state) => state.isInitialized,
		isAthlete:            (state) => state.profile && (state.profile.isAthlete ?? false),
		isFanAccount:         (state) => state.profile?.isFanAccount ?? false,
		isManagerAccount:     (state) => state.profile?.isManagerAccount ?? false,
		fanAccountGeoblocked: (state) => state.fanAccountGeoblocked,
		hasAssumedUser:       (state) => state.profile && state.profile.originUserId,
		originUserId:         (state) => state.profile && state.profile.originUserId ? state.profile.originUserId : null,
		loginRedirectUrl:     () => storage.loginRedirectUrl,
		purchasedEvents:      (state) => state.profile && state.profile.accessibleResources && state.profile.accessibleResources.eventIds ? state.profile.accessibleResources.eventIds.bought : [],
		subscriptions:        (state) => state.profile && state.profile.accessibleResources?.subscriptionIds ? state.profile.accessibleResources.subscriptionIds : [],
		followings:           (state) => state.profile?.followings,
		hasAccessToEvent:     (state) => (event) => {
			// Pay per view or subscription or external access
			return hasSubscriptionForEvent(state.profile?.accessibleResources, event) ||
				hasPpv(state.profile?.accessibleResources, event) ||
				hasExternalAccessForEvent(state.profile?.accessibleResources, event);
		},
		hasBookmarkedEvent: (state) => (event) => {
			return state.profile?.bookmarks?.some(bookmark => bookmark.id === event.id && bookmark.type === 'event');
		},
	},
};
